import React, { useState, useEffect, useCallback } from "react";

const ButtonLeft = (props) => {
    return (
        <button {...props} className={`btn btn-light border border-dark p-2 mx-1 rounded-circle ${props.className}`} aria-label="Left">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="tabler-icon tabler-icon-arrow-left"
            >
                <path d="M19 12H5"></path>
                <path d="M12 19L5 12"></path>
                <path d="M12 5L5 12"></path>
            </svg>
        </button>
    );
}

const ButtonRight = (props) => {
    return (
        <button {...props} className={`btn btn-light border border-dark p-2 mx-1 rounded-circle ${props.className}`} aria-label="Right">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="tabler-icon tabler-icon-arrow-right"
            >
                <path d="M5 12H19"></path>
                <path d="M12 5L19 12"></path>
                <path d="M12 19L19 12"></path>
            </svg>
        </button>
    );
}

const ContentSlider = ({
    items,
    itemCountToShow = 2,
    renderItemTemplate,
    showMoreUrl,
    showMoreText = "Show More",
    wrapForwardOnly = false,
}) => {
    const [startIndex, setStartIndex] = useState(0);

    // Responsive item count using Bootstrap breakpoints
    const getResponsiveItemCount = useCallback(() => {
        if (window.innerWidth < 576) return 1; // xs: 1 item
        if (window.innerWidth < 768) return 2; // sm: 2 items
        if (window.innerWidth < 992) return 2; // md: 2 items
        return itemCountToShow; // lg+: original count
    }, [itemCountToShow]);
    
    const [responsiveItemCount, setResponsiveItemCount] = useState(getResponsiveItemCount());
    
    // Update responsive item count on window resize
    useEffect(() => {
        const handleResize = () => {
            setResponsiveItemCount(getResponsiveItemCount());
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [itemCountToShow, getResponsiveItemCount]);

    const itemCount = items.length;
    const itemsToShow = Math.min(responsiveItemCount, itemCount);
    const maxStartIndex = Math.max(0, itemCount - itemsToShow);
    const atEnd = startIndex >= maxStartIndex;
    const canGoPrev = !wrapForwardOnly || startIndex > 0;

    useEffect(() => {
        if (wrapForwardOnly && startIndex > maxStartIndex) {
            setStartIndex(maxStartIndex);
        }
    }, [wrapForwardOnly, startIndex, maxStartIndex]);

    const next = () => {
        if (wrapForwardOnly) {
            setStartIndex(atEnd ? 0 : startIndex + 1);
            return;
        }
        setStartIndex((startIndex + 1) % itemCount);
    };

    const prev = () => {
        if (wrapForwardOnly) {
            if (startIndex > 0) {
                setStartIndex(startIndex - 1);
            }
            return;
        }
        setStartIndex((startIndex - 1 + itemCount) % itemCount);
    };

    const visibleItems = [];
    for (let i = 0; i < itemsToShow; i++) {
        const index = wrapForwardOnly ? startIndex + i : (startIndex + i) % itemCount;
        visibleItems.push(items[index]);
    }

    return (
        <div>
            <div className="row">
                {visibleItems.map((item, index) => (
                    <React.Fragment key={item.id || index}>
                        {renderItemTemplate(item, index)}
                    </React.Fragment>
                ))}
            </div>
            <div className="row mt-3 me-3 justify-content-end">
                {showMoreUrl && (
                    <div className="col-auto p-0 me-2 d-flex align-items-center">
                        <a href={showMoreUrl}>
                            {showMoreText}
                        </a>
                    </div>
                )}
                {canGoPrev && (
                    <div className="col-auto p-0">
                        <ButtonLeft onClick={prev} />
                    </div>
                )}
                <div className="col-auto p-0">
                    <ButtonRight onClick={next} />
                </div>
            </div>
        </div>
    );
};

export default ContentSlider;
